import { type UseSuspenseQueryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";

import { tableQueryOptions } from "@/web/lib/tanstack";
import { tables } from "@tycho/web-bindings";
import type {
  BrandsV4,
  CardEngineInput,
  OperatingPropertiesJobStatusForUserRow,
  OperatingPropertyForUserRow,
  ShareBusinessCardContextRow,
  SmbSuperViewElement,
  TargetablePropertyForUserRow,
  UnderwritableEntitiesV4,
} from "@tycho/web-bindings/types";

/**
 * The read seam for the admin SMB viewer.
 *
 * The SMB dashboard's data comes from three SpacetimeDB views that filter by
 * the *caller's* identity (`smb_super_view`, `card_engine_inputs`,
 * `all_underwritable_entities_for_user`) — SpacetimeDB can't take view args, so
 * an admin subscribing to them only ever sees their own data (and
 * `smb_super_view` even *throws* for a caller with no business). The
 * `/admin/smb/$brandId` route instead fetches the target brand's data via the
 * `adminBrandSnapshot` procedure and injects it here.
 *
 * The dashboard read hooks below consult this context and fall back to the live
 * subscription when it's absent — so the normal user path is byte-for-byte
 * unchanged (no provider ⇒ same `useSuspenseQuery` as before). When a snapshot
 * *is* present, the live views are never subscribed: the hooks read a
 * resolve-immediately placeholder query (so suspense/hook order stays stable)
 * and return the injected data instead.
 */
export type AdminSmbData = {
  readonly superView: readonly SmbSuperViewElement[];
  readonly cardEngineInputs: readonly CardEngineInput[];
  readonly underwritableEntities: readonly UnderwritableEntitiesV4[];
  readonly brands: readonly BrandsV4[];
  readonly shareBusinessCardContexts: readonly ShareBusinessCardContextRow[];
  readonly targetableProperties: readonly TargetablePropertyForUserRow[];
  readonly operatingProperties: readonly OperatingPropertyForUserRow[];
  readonly operatingPropertiesJobStatus: readonly OperatingPropertiesJobStatusForUserRow[];
};

const AdminSmbDataContext = createContext<AdminSmbData | null>(null);

export function AdminSmbDataProvider({
  value,
  children,
}: {
  value: AdminSmbData;
  children: React.ReactNode;
}) {
  return <AdminSmbDataContext value={value}>{children}</AdminSmbDataContext>;
}

/** The injected admin snapshot, or `null` in the normal (live) user path. */
export function useAdminSmbData(): AdminSmbData | null {
  return useContext(AdminSmbDataContext);
}

/**
 * A suspense-query options object that resolves immediately to `[]` without
 * touching SpacetimeDB — the stand-in for a live view subscription while an
 * admin snapshot is supplying the real data.
 *
 * It is cast to the live options' type (`placeholderFor`): the two ternary
 * branches must share one type for `useSuspenseQuery`'s inference, and the
 * placeholder's `[]` is always discarded when `admin` is set, so the cast is
 * sound (we never read the value it produces).
 */
function injectedPlaceholder(key: string): UseSuspenseQueryOptions<never[], Error> {
  return {
    queryKey: ["admin-injected", key],
    queryFn: () => Promise.resolve([]),
    staleTime: Number.POSITIVE_INFINITY,
  };
}

function placeholderFor<O>(key: string, _liveOptions: O): O {
  return injectedPlaceholder(key) as unknown as O;
}

/**
 * The one implementation behind every seam hook below: read the live view for
 * the signed-in user, or return the injected admin snapshot slice inside the
 * `/admin/smb/$brandId` viewer (never subscribing the caller-scoped view).
 */
function useLiveOrInjected<TRow>(
  liveOptions: UseSuspenseQueryOptions<TRow[], Error>,
  placeholder: UseSuspenseQueryOptions<TRow[], Error>,
  pick: (admin: AdminSmbData) => readonly TRow[],
): readonly TRow[] {
  const admin = useAdminSmbData();
  const { data } = useSuspenseQuery(admin ? placeholder : liveOptions);
  return admin ? pick(admin) : data;
}

const allUnderwritableEntitiesOptions = tableQueryOptions(tables.allUnderwritableEntitiesForUser);
const cardEngineInputsOptions = tableQueryOptions(tables.cardEngineInputs);
const smbSuperViewOptions = tableQueryOptions(tables.smbSuperView);
const allBrandsForUserOptions = tableQueryOptions(tables.allBrandsForUser);
const shareBusinessCardContextsOptions = tableQueryOptions(tables.shareBusinessCardContexts);
const allTargetablePropertiesOptions = tableQueryOptions(tables.allTargetablePropertiesForUser);
const allOperatingPropertiesOptions = tableQueryOptions(tables.allOperatingPropertiesForUser);
const operatingPropertiesJobStatusOptions = tableQueryOptions(
  tables.operatingPropertiesJobStatusForUser,
);

const underwritableEntitiesPlaceholder = placeholderFor(
  "uw-entities",
  allUnderwritableEntitiesOptions,
);
const cardEngineInputsPlaceholder = placeholderFor("card-engine-inputs", cardEngineInputsOptions);
const smbSuperViewPlaceholder = placeholderFor("smb-super-view", smbSuperViewOptions);
const allBrandsPlaceholder = placeholderFor("brands", allBrandsForUserOptions);
const shareContextsPlaceholder = placeholderFor("share-contexts", shareBusinessCardContextsOptions);
const targetablePropertiesPlaceholder = placeholderFor(
  "targetable-properties",
  allTargetablePropertiesOptions,
);
const operatingPropertiesPlaceholder = placeholderFor(
  "operating-properties",
  allOperatingPropertiesOptions,
);
const operatingJobStatusPlaceholder = placeholderFor(
  "operating-job-status",
  operatingPropertiesJobStatusOptions,
);

/** All underwritable entities for the active business. */
export function useSuspendedUnderwritableEntities(): readonly UnderwritableEntitiesV4[] {
  return useLiveOrInjected(
    allUnderwritableEntitiesOptions,
    underwritableEntitiesPlaceholder,
    (a) => a.underwritableEntities,
  );
}

/** Card-engine inputs for the active business. */
export function useSuspendedCardEngineInputs(): readonly CardEngineInput[] {
  return useLiveOrInjected(
    cardEngineInputsOptions,
    cardEngineInputsPlaceholder,
    (a) => a.cardEngineInputs,
  );
}

/**
 * The full `smb_super_view` row set. Prefer {@link useSuspendedSmbSuperView} for
 * the per-entity lookup.
 */
export function useSuspendedSmbSuperViewRows(): readonly SmbSuperViewElement[] {
  return useLiveOrInjected(smbSuperViewOptions, smbSuperViewPlaceholder, (a) => a.superView);
}

/** Brands the caller belongs to (the single target brand inside the viewer). */
export function useSuspendedBrandsForUser(): readonly BrandsV4[] {
  return useLiveOrInjected(allBrandsForUserOptions, allBrandsPlaceholder, (a) => a.brands);
}

/** Share-business-card contexts for the active business. */
export function useSuspendedShareBusinessCardContexts(): readonly ShareBusinessCardContextRow[] {
  return useLiveOrInjected(
    shareBusinessCardContextsOptions,
    shareContextsPlaceholder,
    (a) => a.shareBusinessCardContexts,
  );
}

/** Targetable-property catalog across the active business's brand. */
export function useSuspendedTargetableProperties(): readonly TargetablePropertyForUserRow[] {
  return useLiveOrInjected(
    allTargetablePropertiesOptions,
    targetablePropertiesPlaceholder,
    (a) => a.targetableProperties,
  );
}

/** Discovered operating properties for the active business. */
export function useSuspendedOperatingProperties(): readonly OperatingPropertyForUserRow[] {
  return useLiveOrInjected(
    allOperatingPropertiesOptions,
    operatingPropertiesPlaceholder,
    (a) => a.operatingProperties,
  );
}

/** Per-brand operating-locations discovery-job status. */
export function useSuspendedOperatingPropertiesJobStatus(): readonly OperatingPropertiesJobStatusForUserRow[] {
  return useLiveOrInjected(
    operatingPropertiesJobStatusOptions,
    operatingJobStatusPlaceholder,
    (a) => a.operatingPropertiesJobStatus,
  );
}
